// api.ts — talks to our own backend at /api/turn.
// The backend holds the Anthropic API key; the frontend never sees it.
//
// All requests are authed via the shared authedFetch helper, which handles:
//   - Authorization: Bearer header
//   - one-shot refresh on 401
//   - clear+reload on second 401
//
// 402 handling: when the server reports the user has run out of turns
// (`{ error: "out_of_turns", code: "out_of_turns" }`), we surface a typed
// OutOfTurnsError that App.tsx instanceof-checks to open the Paywall modal.
// We deliberately don't push it into the in-game log — the modal IS the
// messaging.

import { authedFetch } from "./auth/tokens";
import type { CondensedState, StateDelta, SequelOption } from "./engine";

export interface EngineResponse {
  narration: string;
  stateDelta: StateDelta;
  deathOrWin?: "win" | "death" | null;
  /**
   * Chapters generated since the previous /api/turn. Server-side bookshelf
   * wiring (story_chapters rows whose generated_at > the client's
   * `lastSeenChapterAt`). The web app reads this on each successful turn
   * and fires one toast per entry — backfill batches are aggregated by
   * the server so a single response carries one entry with chapterCount
   * set rather than N separate entries.
   *
   * Stamped on the OUTER `/api/turn` response by the server route; we
   * lift it onto EngineResponse here so callers consume a single object.
   */
  newChaptersSinceLastTurn?: NewChapterRef[];
  /**
   * Phase 3 — referral bonus deposits queued in Redis since the previous
   * /api/turn. Each entry is a single deposit (signup or monthly). The
   * client renders an action toast per entry, or a coalesced toast when
   * >3 land in a single session loading window. See ReferralsPage.
   */
  newReferralBonusesSinceLastTurn?: ReferralBonusToast[];
  /**
   * Phase 3b — the "next book" slate for a WINNING turn. The server generates
   * + persists these when a turn fires the ending (deathOrWin:"win") and the
   * archetype-aware propensity gate passes; empty/absent otherwise (a
   * non-winning turn, a fail-roll, an honestly-closed story, or any
   * generation failure). App.tsx holds these alongside `gameOver` and passes
   * them to the Finale's sequel cards. Defaults to [] when the server omits
   * the field. Lifted from the OUTER /api/turn envelope (like
   * newChaptersSinceLastTurn), shape-checked defensively here.
   */
  sequelOptions: SequelOption[];
}

/**
 * Per-deposit toast payload from /api/turn (lifted from Redis). Matches
 * server services/referralBonus.ts:PendingReferralToast.
 */
export interface ReferralBonusToast {
  refereeFirstName: string;
  turnsDeposited: number;
  reason: "signup" | "monthly";
  bookSlug?: string;
  atIso: string;
}

/**
 * Chapter reference attached to a per-turn response. The toast pipeline
 * consumes these to fire bookshelf notifications. `isBackfill` distinguishes
 * the batch-toast case ("N chapters added") from the per-chapter case
 * ("Chapter 7 is ready").
 */
export interface NewChapterRef {
  storyId: string;
  chapterNumber: number;
  title: string;
  isFirst: boolean;
  isBackfill: boolean;
  /** Batch size; set on the first entry of a backfill batch only. */
  chapterCount?: number;
}

/**
 * Thrown by askEngine when /api/turn returns 402 with code "out_of_turns".
 * App.tsx catches this with instanceof and opens the Paywall modal instead
 * of treating it as a generic engine error.
 */
export class OutOfTurnsError extends Error {
  readonly code = "out_of_turns" as const;
  constructor(message = "out_of_turns") {
    super(message);
    this.name = "OutOfTurnsError";
    // Preserve prototype chain across the TS→ES5-ish target so instanceof works.
    Object.setPrototypeOf(this, OutOfTurnsError.prototype);
  }
}

/**
 * Thrown by askEngine when /api/turn returns 402 with code
 * "subscription_required" — the forever-default scenario lock
 * (STORIES_CONTRACT §1). A free user tried to submit a turn against a
 * story whose scenarioId doesn't match their forever_default_scenario_id.
 * App.tsx catches this with instanceof and opens the Paywall modal with
 * reason "story_locked" rather than the generic out-of-turns variant.
 */
export class SubscriptionRequiredError extends Error {
  readonly code = "subscription_required" as const;
  constructor(message = "subscription_required") {
    super(message);
    this.name = "SubscriptionRequiredError";
    Object.setPrototypeOf(this, SubscriptionRequiredError.prototype);
  }
}

/**
 * Thrown by askEngine when /api/turn returns 503 with code "upstream_capacity".
 * The server fires this when the upstream model provider (Anthropic) is out
 * of capacity — typically credit-balance exhausted. App.tsx catches this and
 * shows a friendly toast ("the world is catching its breath") rather than
 * a red engine-error log line. The turn was already refunded server-side.
 */
export class UpstreamCapacityError extends Error {
  readonly code = "upstream_capacity" as const;
  constructor(message = "upstream_capacity") {
    super(message);
    this.name = "UpstreamCapacityError";
    Object.setPrototypeOf(this, UpstreamCapacityError.prototype);
  }
}

// Robust JSON extraction — handles fences, leading/trailing prose.
// The model is instructed to return raw JSON, but defensive parsing is cheap insurance.
export function extractJSON<T = unknown>(text: string): T {
  if (!text) throw new Error("Empty response from model");
  const cleaned = text.replace(/```json\s*/gi, "").replace(/```/g, "").trim();
  const start = cleaned.indexOf("{");
  if (start === -1) throw new Error("No JSON object in response");

  let depth = 0;
  let inString = false;
  let escape = false;
  let end = -1;
  for (let i = start; i < cleaned.length; i++) {
    const ch = cleaned[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === "\\") {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (inString) continue;
    if (ch === "{") depth++;
    else if (ch === "}") {
      depth--;
      if (depth === 0) {
        end = i;
        break;
      }
    }
  }
  if (end === -1) throw new Error("Unbalanced JSON in response");
  return JSON.parse(cleaned.slice(start, end + 1)) as T;
}

// Send the condensed state + player input to our backend; get a parsed
// engine response back. Throws on network/API/parse failure.
// On 402 with code "out_of_turns" → throws OutOfTurnsError.
//
// `lastSeenChapterAt` is an ISO timestamp the server uses to compute
// `newChaptersSinceLastTurn` — the chapters that have landed in
// story_chapters since the client's last seen point. Stateless; the
// server doesn't track this itself. App.tsx maintains the cursor in a
// ref and bumps it after consuming each response.
export async function askEngine(
  condensedState: CondensedState,
  playerInput: string,
  lastSeenChapterAt?: string,
): Promise<EngineResponse> {
  let response: Response;
  try {
    response = await authedFetch(`/api/turn`, {
      method: "POST",
      body: JSON.stringify({
        state: condensedState,
        input: playerInput,
        ...(lastSeenChapterAt ? { lastSeenChapterAt } : {}),
      }),
    });
  } catch (netErr: unknown) {
    const msg = netErr instanceof Error ? netErr.message : String(netErr);
    throw new Error(`Network error: ${msg}`);
  }

  if (!response.ok) {
    const errText = await response.text().catch(() => "");

    // Check for the paywall trigger before falling through to a generic error.
    if (response.status === 402) {
      let parsed: { error?: string; code?: string } = {};
      try {
        parsed = JSON.parse(errText) as { error?: string; code?: string };
      } catch {
        // Body wasn't JSON — fall through to OutOfTurnsError anyway, since 402
        // semantically means payment required.
      }
      // Forever-default scenario lock — distinct from out-of-turns. See
      // STORIES_CONTRACT §1.
      if (parsed.code === "subscription_required") {
        throw new SubscriptionRequiredError(
          parsed.error || "subscription_required",
        );
      }
      if (parsed.code === "out_of_turns" || parsed.error === "out_of_turns") {
        throw new OutOfTurnsError(parsed.error || "out_of_turns");
      }
      // 402 without the expected code shape — still treat as out-of-turns; the
      // UX is the same and it avoids leaking a confusing raw error to the log.
      throw new OutOfTurnsError("out_of_turns");
    }

    // Upstream capacity error (Anthropic out of credit, etc.) — server has
    // already refunded the turn; we just surface a friendly toast.
    if (response.status === 503) {
      let parsed: { error?: string; code?: string } = {};
      try {
        parsed = JSON.parse(errText) as { error?: string; code?: string };
      } catch {
        // Body wasn't JSON — fall through to a generic error; the operator
        // didn't intend a capacity message here.
      }
      if (parsed.code === "upstream_capacity") {
        throw new UpstreamCapacityError(
          parsed.error || "The world is catching its breath.",
        );
      }
      // Unrecognised 503 — fall through to the generic handler below.
    }

    throw new Error(`Server ${response.status}: ${errText.slice(0, 300)}`);
  }

  const payload = (await response.json()) as {
    error?: string;
    text?: unknown;
    newChaptersSinceLastTurn?: unknown;
    newReferralBonusesSinceLastTurn?: unknown;
    sequelOptions?: unknown;
  };
  if (payload.error) {
    throw new Error(`Server error: ${payload.error}`);
  }
  if (typeof payload.text !== "string") {
    throw new Error(
      `Malformed server response: ${JSON.stringify(payload).slice(0, 200)}`
    );
  }

  const parsed = extractJSON<EngineResponse>(payload.text);
  // The inner model JSON never carries the slate — it lives on the outer
  // envelope. Default to [] so callers always see an array.
  parsed.sequelOptions = Array.isArray(payload.sequelOptions)
    ? payload.sequelOptions.filter((o): o is SequelOption => isValidSequelOption(o))
    : [];
  // Lift the server's outer-envelope field onto the parsed engine response so
  // callers see one cohesive object. Defensive: shape-check the array entries
  // so a server-side schema drift doesn't blow up the turn loop.
  if (Array.isArray(payload.newChaptersSinceLastTurn)) {
    parsed.newChaptersSinceLastTurn = payload.newChaptersSinceLastTurn
      .filter((c): c is NewChapterRef => isValidChapterRef(c));
  }
  if (Array.isArray(payload.newReferralBonusesSinceLastTurn)) {
    parsed.newReferralBonusesSinceLastTurn =
      payload.newReferralBonusesSinceLastTurn.filter(
        (b): b is ReferralBonusToast => isValidBonusToast(b),
      );
  }
  return parsed;
}

function isValidChapterRef(c: unknown): c is NewChapterRef {
  if (!c || typeof c !== "object") return false;
  const o = c as Record<string, unknown>;
  return (
    typeof o.storyId === "string" &&
    typeof o.chapterNumber === "number" &&
    typeof o.title === "string" &&
    typeof o.isFirst === "boolean" &&
    typeof o.isBackfill === "boolean"
  );
}

function isValidSequelOption(o: unknown): o is SequelOption {
  if (!o || typeof o !== "object") return false;
  const r = o as Record<string, unknown>;
  return (
    typeof r.id === "string" &&
    typeof r.structure === "string" &&
    typeof r.title === "string" &&
    typeof r.pitch === "string" &&
    typeof r.newTarget === "string" &&
    typeof r.playAs === "string" &&
    Array.isArray(r.carries)
  );
}

function isValidBonusToast(b: unknown): b is ReferralBonusToast {
  if (!b || typeof b !== "object") return false;
  const o = b as Record<string, unknown>;
  return (
    typeof o.refereeFirstName === "string" &&
    typeof o.turnsDeposited === "number" &&
    (o.reason === "signup" || o.reason === "monthly") &&
    typeof o.atIso === "string"
  );
}
